import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from '@tanstack/react-router';
import type { ColumnDef } from '@tanstack/react-table';
import { Calendar, Clock, Plus, Stethoscope, UserRound } from 'lucide-react';
import { toast } from 'sonner';

import { Badge } from '@/components/ui/badge';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { useOrgDateFormat } from '@/lib/timezone';
import { formatCalendarDate } from '@/lib/utils/date';
import { P, RequirePermission } from '@/features/permissions';
import { usePageHeaderSetter } from '@/components/layout/PageHeaderContext';
import { type EncounterOut, useBulkDeleteEncounters, useEncounters } from '../api/encounters-api';
import { SimpleTable } from '@/components/table/simple-table';
import { getSelectionColumn } from '@/components/table/selection-column';
import { TableBulkActions, bulkDeleteAction } from '@/components/table/bulk-action-bar';
import { TableFilterSection } from '@/components/table/table-filter-section';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { encounterTypeColors, encounterStatusColors, encounterTypeStatColors } from '../constants';
import { StatsCard } from '../components/StatsCard';
import { SearchInput } from '../components/SearchInput';
import { PatientAvatar } from '../components/PatientAvatar';
import { LoadingState } from '../components/LoadingState';
import { RecordCount } from '../components/RecordCount';
import { DateRangeFilter } from '../components/DateRangeFilter';
import { EncounterCard } from '../components/EncounterCard';

const encounterTypeIcons: Record<string, typeof Stethoscope> = {
  telehealth: Clock,
  urgent: Calendar,
};

// The `created` column's cell renderer is a plain function inside a
// TanStack column definition, not a component — `useOrgDateFormat` (a hook)
// can't be called there. Extracted so the hook has a component to live in.
function CreatedCell({ value }: { value: string }) {
  const { formatInstant } = useOrgDateFormat();
  return <span className="text-sm text-muted-foreground">{formatInstant(value, 'MMM d')}</span>;
}

const columns: ColumnDef<EncounterOut>[] = [
  getSelectionColumn<EncounterOut>(),
  {
    id: 'date',
    accessorKey: 'encounter_date',
    header: 'Date',
    size: 120,
    cell: ({ row }) => <span className="text-sm font-medium">{formatCalendarDate(row.original.encounter_date)}</span>,
  },
  {
    id: 'patient',
    header: 'Patient',
    accessorFn: (row) => row.patient?.name ?? row.patient?.mrn ?? '',
    cell: ({ row }) => {
      const patientName = row.original.patient?.name ?? row.original.patient?.mrn ?? '—';
      return (
        <div className="flex items-center gap-3">
          <PatientAvatar name={row.original.patient?.name} />
          <div className="min-w-0">
            <p className="font-medium truncate">{patientName}</p>
            {row.original.patient?.mrn && row.original.patient.name && (
              <p className="text-xs text-muted-foreground truncate">{row.original.patient.mrn}</p>
            )}
          </div>
        </div>
      );
    },
  },
  {
    id: 'clinician',
    header: 'Clinician',
    accessorFn: (row) => row.clinician?.name ?? '',
    cell: ({ row }) =>
      row.original.clinician ? (
        <div className="flex items-center gap-2">
          <UserRound className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
          <span className="text-sm">{row.original.clinician.name}</span>
        </div>
      ) : (
        <span className="text-sm text-muted-foreground">Unassigned</span>
      ),
  },
  {
    id: 'type',
    accessorKey: 'encounter_type',
    header: 'Type',
    cell: ({ row }) => {
      const encounterType = row.original.encounter_type ?? 'initial';
      return (
        <Badge variant="secondary" className={`text-xs capitalize ${encounterTypeColors[encounterType] ?? ''}`}>
          {encounterType}
        </Badge>
      );
    },
  },
  {
    id: 'status',
    accessorKey: 'status',
    header: 'Status',
    cell: ({ row }) => {
      const status = row.original.status ?? 'draft';
      return (
        <Badge
          variant="secondary"
          className={`text-xs capitalize ${encounterStatusColors[status] ?? 'bg-muted text-muted-foreground'}`}>
          {status}
        </Badge>
      );
    },
  },
  {
    id: 'created',
    accessorKey: 'created_at',
    header: 'Created',
    cell: ({ row }) => <CreatedCell value={row.original.created_at} />,
  },
];

export function EncountersPage() {
  const navigate = useNavigate();
  const setPageHeader = usePageHeaderSetter();
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [clinicianFilter, setClinicianFilter] = useState<string>('all');
  const [search, setSearch] = useState('');
  const [dateFrom, setDateFrom] = useState<string | null>(null);
  const [dateTo, setDateTo] = useState<string | null>(null);
  const { data, isLoading } = useEncounters({
    limit: 100,
    ...(dateFrom && { date_from: dateFrom }),
    ...(dateTo && { date_to: dateTo }),
  });
  const bulkDelete = useBulkDeleteEncounters();

  useEffect(() => {
    setPageHeader({
      title: 'Encounters',
      actions: (
        <RequirePermission permission={P.CREATE_ENCOUNTER}>
          <Button size="sm" onClick={() => navigate({ to: '/encounters/new' })}>
            <Plus className="mr-2 h-4 w-4" />
            New Encounter
          </Button>
        </RequirePermission>
      ),
    });
    return () => setPageHeader(null);
  }, [setPageHeader, navigate]);

  const encounters = data?.data ?? [];

  const filteredEncounters = useMemo(() => {
    let filtered = encounters;
    if (statusFilter !== 'all') {
      filtered = filtered.filter((e) => e.status === statusFilter);
    }
    if (clinicianFilter !== 'all') {
      filtered = filtered.filter((e) => e.clinician?.name === clinicianFilter);
    }
    if (search.trim()) {
      const q = search.toLowerCase();
      filtered = filtered.filter(
        (e) =>
          e.patient?.name?.toLowerCase().includes(q) ||
          e.patient?.mrn.toLowerCase().includes(q) ||
          e.clinician?.name.toLowerCase().includes(q),
      );
    }
    return filtered;
  }, [encounters, statusFilter, clinicianFilter, search]);

  const stats = useMemo(() => {
    const types: Record<string, number> = {};
    for (const e of encounters) {
      const t = e.encounter_type ?? 'untyped';
      types[t] = (types[t] || 0) + 1;
    }
    return types;
  }, [encounters]);

  const clinicians = useMemo(() => {
    const names = new Set<string>();
    for (const e of encounters) {
      if (e.clinician?.name) names.add(e.clinician.name);
    }
    return [...names].sort();
  }, [encounters]);

  const activeFilterCount =
    (dateFrom !== null || dateTo !== null ? 1 : 0) +
    (statusFilter !== 'all' ? 1 : 0) +
    (clinicianFilter !== 'all' ? 1 : 0);

  const clearAllFilters = () => {
    setDateFrom(null);
    setDateTo(null);
    setStatusFilter('all');
    setClinicianFilter('all');
  };

  if (isLoading) return <LoadingState />;

  return (
    <div className="flex flex-col gap-6 p-4 md:p-6">
      {/* Stats */}
      <div className="grid gap-4 grid-cols-2 md:grid-cols-4">
        <StatsCard icon={Stethoscope} label="Total" value={data?.meta.total_items ?? 0} />
        {Object.entries(stats)
          .slice(0, 3)
          .map(([type, count]) => (
            <StatsCard
              key={type}
              icon={encounterTypeIcons[type] ?? Stethoscope}
              label={type}
              value={count}
              iconClassName={encounterTypeStatColors[type] ?? 'bg-muted text-muted-foreground'}
            />
          ))}
      </div>

      {/* Search + Filters */}
      <div className="flex flex-col gap-3">
        <SearchInput value={search} onChange={setSearch} placeholder="Search by patient, MRN, or clinician..." />
        <TableFilterSection activeFilterCount={activeFilterCount} onClearAll={clearAllFilters}>
          <div className="flex flex-col gap-1.5">
            <Label className="text-xs font-medium text-muted-foreground">Date Range</Label>
            <DateRangeFilter
              value={{ dateFrom, dateTo }}
              onChange={({ dateFrom: df, dateTo: dt }) => {
                setDateFrom(df);
                setDateTo(dt);
              }}
            />
          </div>
          <div className="flex flex-col gap-1.5">
            <Label className="text-xs font-medium text-muted-foreground">Status</Label>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-full sm:w-[160px]">
                <SelectValue placeholder="All Statuses" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Statuses</SelectItem>
                <SelectItem value="draft">Draft</SelectItem>
                <SelectItem value="submitted">Submitted</SelectItem>
                <SelectItem value="complete">Complete</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="flex flex-col gap-1.5">
            <Label className="text-xs font-medium text-muted-foreground">Clinician</Label>
            <Select value={clinicianFilter} onValueChange={setClinicianFilter}>
              <SelectTrigger className="w-full sm:w-[180px]">
                <SelectValue placeholder="All Clinicians" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Clinicians</SelectItem>
                {clinicians.map((name) => (
                  <SelectItem key={name} value={name}>
                    {name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </TableFilterSection>
      </div>

      {/* Table — desktop */}
      <div className="hidden md:block">
        <SimpleTable<EncounterOut>
          data={filteredEncounters}
          columns={columns}
          getRowId={(row) => row.id}
          onRowClick={(row) => navigate({ to: '/encounters/$encounterId', params: { encounterId: row.id } })}
          actions={(table) => (
            <TableBulkActions
              table={table}
              actions={(rows) => [
                bulkDeleteAction({
                  rows,
                  entityName: 'encounter',
                  getId: (r) => r.id,
                  onDelete: async (ids) => {
                    await bulkDelete.mutateAsync(ids);
                    table.resetRowSelection();
                    toast.success(`Deleted ${ids.length} encounter${ids.length === 1 ? '' : 's'}`);
                  },
                }),
              ]}
            />
          )}
        />
      </div>

      {/* Cards — mobile */}
      <div className="flex flex-col gap-3 md:hidden">
        {filteredEncounters.map((encounter) => (
          <EncounterCard
            key={encounter.id}
            encounter={encounter}
            onClick={() => navigate({ to: '/encounters/$encounterId', params: { encounterId: encounter.id } })}
          />
        ))}
      </div>

      <RecordCount filtered={filteredEncounters.length} total={data?.meta.total_items ?? 0} entity="encounters" />
    </div>
  );
}
